import { useState } from 'react';

export type SortDirection = 'asc' | 'desc';
type SortValue = string | number | null;

/** Sorts rows by one column; clicking cycles descending → ascending → original order. */
export function useSortedRows<T, K extends string>(rows: T[], valueOf: (row: T, key: K) => SortValue) {
  const [sort, setSort] = useState<{ key: K; direction: SortDirection } | null>(null);
  // Rows with no value always go last.
  const sortRows = (list: T[]): T[] => {
    if (!sort) return list;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const x = valueOf(a, sort.key);
      const y = valueOf(b, sort.key);
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      return (typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y) : Number(x) - Number(y)) * factor;
    });
  };
  const sorted = sortRows(rows);
  const sortProps = (key: K) => ({
    direction: sort?.key === key ? sort.direction : null,
    onSort: () => setSort((current) => (current?.key !== key ? { key, direction: 'desc' } : current.direction === 'desc' ? { key, direction: 'asc' } : null)),
  });
  /** `sortRows` orders nested rows (a category's sub-categories) by the same column. */
  return { sorted, sortRows, sortProps };
}
